import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Pencil, Trash2, Receipt, Plus, X, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';

const fC = (v) => {
  if (!v || v === 0) return '₹0';
  const n = Number(v);
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2).replace(/\.?0+$/, '')}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1).replace(/\.?0+$/, '')}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${n.toFixed(0)}`;
};

/**
 * Division-level Realised Revenue Ledger
 * Lists every b2b_revenue entry for the whole division (regardless of outlet field)
 * with per-row Edit + Delete so the CEO can correct data without drilling into outlets.
 *
 * Primary use-case: fix entries whose `outlet` field accidentally got stored as the
 * division id (e.g., "cc-ops") — they'd be invisible from any outlet page otherwise.
 */
export default function DivisionRealisedLedger({ divisionId }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ month: '', amount: '', invoice_number: '', po_number: '', notes: '', entry_type: 'realised' });
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/b2b-revenue?division_id=${divisionId}`);
      setEntries(res.data);
      // Auto-expand the ledger if the division has stale 'realised' entries with no invoice/PO
      const suspect = (res.data || []).some(e => e.entry_type === 'realised' && !e.invoice_number && !e.po_number);
      if (suspect) setExpanded(true);
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (divisionId) refresh(); }, [divisionId]);

  const startEdit = (e) => {
    setEditId(e.id);
    setForm({
      month: e.month || '',
      amount: e.amount ?? '',
      invoice_number: e.invoice_number || '',
      po_number: e.po_number || '',
      notes: e.notes || '',
      entry_type: e.entry_type || 'realised',
    });
  };

  const cancelEdit = () => { setEditId(null); setForm({ month: '', amount: '', invoice_number: '', po_number: '', notes: '', entry_type: 'realised' }); };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.put(`/b2b-revenue/${editId}`, {
        month: form.month,
        amount: parseFloat(form.amount) || 0,
        invoice_number: form.invoice_number,
        po_number: form.po_number,
        notes: form.notes,
        entry_type: form.entry_type,
      });
      cancelEdit();
      await refresh();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete "${e.deal_name || e.client_name}" · ${e.month} · ${fC(e.amount)}?\n\nThis permanently removes the entry from the ledger.`)) return;
    setBusy(true);
    try {
      await api.delete(`/b2b-revenue/${e.id}`);
      await refresh();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;
  if (entries.length === 0) return null;

  const suspectCount = entries.filter(e => e.entry_type === 'realised' && !e.invoice_number && !e.po_number).length;
  const totalRealised = entries.filter(e => e.entry_type === 'realised').reduce((s, e) => s + (e.amount || 0), 0);

  return (
    <div className="bg-white rounded-xl border mb-4" style={{ borderColor: '#E5E1DC' }} data-testid="division-realised-ledger">
      <button
        onClick={() => setExpanded(x => !x)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50"
        data-testid="ledger-toggle">
        <div className="flex items-center gap-2">
          <Receipt size={14} style={{ color: '#2A7B88' }} />
          <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>
            Realised Revenue Ledger <span className="font-normal text-[11px]" style={{ color: '#6B7280' }}>({entries.length} entries · {fC(totalRealised)} realised)</span>
          </p>
          {suspectCount > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FEF2F2', color: '#D4726A' }}>
              <AlertTriangle size={10} /> {suspectCount} realised w/o invoice
            </span>
          )}
        </div>
        {expanded ? <ChevronUp size={14} style={{ color: '#6B7280' }} /> : <ChevronDown size={14} style={{ color: '#6B7280' }} />}
      </button>

      {expanded && (
        <div className="border-t overflow-x-auto" style={{ borderColor: '#F3F4F6' }}>
          <table className="w-full text-xs">
            <thead style={{ backgroundColor: '#F7F5F2' }}>
              <tr>
                <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Month</th>
                <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Deal / Client</th>
                <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Type</th>
                <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Amount</th>
                <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Invoice</th>
                <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>PO</th>
                <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Notes</th>
                <th className="text-right px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
              {entries.map(e => {
                const isEditing = editId === e.id;
                const suspect = e.entry_type === 'realised' && !e.invoice_number && !e.po_number;
                const rowBg = e.entry_type === 'projected' ? '#FFFBEB' : suspect ? '#FEF2F2' : 'transparent';
                if (isEditing) {
                  return (
                    <tr key={e.id} style={{ backgroundColor: '#ECFDF5' }}>
                      <td className="px-3 py-2"><input type="text" value={form.month} onChange={ev => setForm(f => ({ ...f, month: ev.target.value }))} placeholder="YYYY-MM" className="w-24 border rounded px-1.5 py-0.5" style={{ borderColor: '#E5E1DC' }} data-testid={`ledger-month-${e.id}`} /></td>
                      <td className="px-3 py-2 truncate max-w-[160px]" style={{ color: '#6B7280' }}>{e.deal_name || e.client_name || '—'}</td>
                      <td className="px-3 py-2">
                        <select value={form.entry_type} onChange={ev => setForm(f => ({ ...f, entry_type: ev.target.value }))} className="border rounded px-1.5 py-0.5 text-[11px]" style={{ borderColor: '#E5E1DC' }} data-testid={`ledger-type-${e.id}`}>
                          <option value="realised">Realised</option>
                          <option value="projected">Projected</option>
                        </select>
                      </td>
                      <td className="px-3 py-2 text-right"><input type="number" value={form.amount} onChange={ev => setForm(f => ({ ...f, amount: ev.target.value }))} className="w-24 border rounded px-1.5 py-0.5 text-right" style={{ borderColor: '#E5E1DC' }} data-testid={`ledger-amt-${e.id}`} /></td>
                      <td className="px-3 py-2"><input type="text" value={form.invoice_number} onChange={ev => setForm(f => ({ ...f, invoice_number: ev.target.value }))} placeholder="INV-…" className="w-24 border rounded px-1.5 py-0.5" style={{ borderColor: '#E5E1DC' }} /></td>
                      <td className="px-3 py-2"><input type="text" value={form.po_number} onChange={ev => setForm(f => ({ ...f, po_number: ev.target.value }))} placeholder="PO-…" className="w-24 border rounded px-1.5 py-0.5" style={{ borderColor: '#E5E1DC' }} /></td>
                      <td className="px-3 py-2"><input type="text" value={form.notes} onChange={ev => setForm(f => ({ ...f, notes: ev.target.value }))} className="w-32 border rounded px-1.5 py-0.5" style={{ borderColor: '#E5E1DC' }} /></td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1 justify-end">
                          <button onClick={saveEdit} disabled={busy} className="px-2 py-0.5 rounded text-[11px] text-white disabled:opacity-50" style={{ backgroundColor: '#2A7B88' }} data-testid={`ledger-save-${e.id}`}>Save</button>
                          <button onClick={cancelEdit} className="p-1 rounded hover:bg-gray-100" data-testid={`ledger-cancel-${e.id}`}><X size={11} style={{ color: '#6B7280' }} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={e.id} className="hover:bg-gray-50" style={{ backgroundColor: rowBg }} data-testid={`ledger-row-${e.id}`}>
                    <td className="px-3 py-2 font-medium" style={{ color: '#1B2A4A' }}>{e.month}</td>
                    <td className="px-3 py-2 truncate max-w-[160px]" style={{ color: '#6B7280' }}>{e.deal_name || e.client_name || '—'}</td>
                    <td className="px-3 py-2">
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: e.entry_type === 'projected' ? '#FEF3C7' : '#DCFCE7', color: e.entry_type === 'projected' ? '#BA7517' : '#166534' }}>
                        {e.entry_type === 'projected' ? 'PROJECTED' : 'REALISED'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-semibold" style={{ color: e.entry_type === 'projected' ? '#BA7517' : '#1B2A4A' }}>{fC(e.amount)}</td>
                    <td className="px-3 py-2" style={{ color: '#6B7280' }}>{e.invoice_number || <span className="italic text-[10px]" style={{ color: '#D4726A' }}>missing</span>}</td>
                    <td className="px-3 py-2" style={{ color: '#6B7280' }}>{e.po_number || <span className="italic text-[10px]" style={{ color: '#D4726A' }}>missing</span>}</td>
                    <td className="px-3 py-2 truncate max-w-[180px]" style={{ color: '#6B7280' }}>{e.notes || '—'}</td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => startEdit(e)} className="p-1 rounded hover:bg-gray-100" title="Edit entry" data-testid={`ledger-edit-${e.id}`}><Pencil size={11} style={{ color: '#6B7280' }} /></button>
                        <button onClick={() => remove(e)} className="p-1 rounded hover:bg-red-50" title="Delete entry" data-testid={`ledger-delete-${e.id}`}><Trash2 size={11} style={{ color: '#D4726A' }} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {suspectCount > 0 && (
            <div className="px-4 py-2 text-[11px] border-t flex items-center gap-1.5" style={{ borderColor: '#F3F4F6', backgroundColor: '#FFFBEB', color: '#BA7517' }}>
              <AlertTriangle size={11} />
              <span>{suspectCount} realised entries have no invoice/PO — likely false realisations. Edit to add invoice+PO, switch to <b>Projected</b>, or delete.</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
